import { Component, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Place } from '@app/modules/model/place';
import { QrCode } from '@app/modules/model/qrcode';
import { Ticket } from '@app/modules/model/ticket';

import { languageSupport, languageService } from '@app/modules/common/language'
import { toastService } from '@app/modules/common/toast';
import { alertService } from '@app/modules/common/alert';
import { geocodeService } from '@app/modules/common/geocode';
import { viewService } from '@app/modules/view';

@Component({
    selector: 'ui-cart-modify',
    templateUrl:'./ui-cart.html',
    styleUrls: [ 
        './ui-cart.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiTicketCart extends languageSupport implements OnInit, OnChanges, OnDestroy {
    @Input('ticket') _ticket: Ticket = null;

    private _subscriptions = [];

    get place() : Place {
        return this._ticket.place;
    }

    get qrcode() : QrCode {
        return this._ticket.qrcode;
    }

    /***********************************/
    /* DATA COMPONENTS                 */
    /***********************************/

    get IsPrepay(){
        return this._ticket.IsPrepay && (this.view.Access != 'DEVRY');
    }

    get CanSend() {
        return (!this._ticket.IsEmpty);
    }

    /***********************************/
    /* STARTS HERE                     */
    /***********************************/

    constructor(private lang: languageService, private change: ChangeDetectorRef, private toast: toastService, private alertCtrl: alertService, private geocode: geocodeService, public view: viewService) {
        super(lang, change);
    }

    ngOnInit(){
        // nothing to do -- initialize on changes
    }

    ngOnChanges(){
        this._ticket.isopen = true;

        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];
        
        this._subscriptions.push(
            this.place.OnTargetRefresh('TICKET').subscribe(
            data => {
                this.change.markForCheck();
            })
        )

        this._subscriptions.push(
            this._ticket.OnModify.subscribe(
            data => {
                this.AddChange(data);
            })
        )

        this._subscriptions.push(
            this._ticket.OnRefresh.subscribe(
            data => {
                this.change.markForCheck();
            })
        );   
    }

    ngOnDestroy(){
        super.OnDestroy();
        
        this._ticket.isopen = false;
        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];
    }

    /**********************************/
    /* CHANGES CONTROL                */
    /**********************************/

    public _notes = null;

    private _ticketchanges = {
        toInsert: [], 
        toDelete: []
    };

    private AddChange(change){
        if (change.operation == 'add'){
            this._ticketchanges.toInsert.push(change.product);
        }

        if (change.operation == 'del'){
            let _idx = this._ticketchanges.toInsert.indexOf(change.product);
            if (_idx != -1){
                this._ticketchanges.toInsert.splice(_idx, 1);
            }
            else {
                this._ticketchanges.toDelete.push(change.product);
            }
        }
    }

    /***********************************/
    /* EVENT HANDLERS                  */
    /***********************************/

    private get IsPristine(){
        return (this._ticket.products.length == 0);
    }

    @Output('onClose') _onClose = new EventEmitter<any>();     
    private _OnClose(reason){   // 'commit', 'cancel', 'hide'

        this._onClose.emit(reason);
    }

    async OnHide(){
        this._OnClose('hide');
    }

    async OnClose(){
        // on desktop, this will cancel the order
        if (this.view.Desktop){
            if (!this.IsPristine){
                const alert = this.alertCtrl.alert({
                    header: this.lang.tr('@ticket_cancel_confirm_tittle'),
                    message: this.lang.tr('@ticket_cancel_confirm_message'),
                    backdropDismiss: false,
                    buttons: [{
                        text: this.lang.tr('@yes'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(true);
                            }, 100);
        
                            return false;
                        }
                    }, {
                        text: this.lang.tr('@no'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(false);
                            }, 100);
                            return false;
                        }
                    }]
                });
        
                alert.onDidDismiss().then((detail:any) => {
                    if (detail.data == true){
                        this._OnClose('cancel');
                    }
                });
        
                await alert.present();        
            }
            else {
                this._OnClose('cancel');
            }
        }

        // on mobile this will hide the cart view
        if (this.view.Mobile){
            this._OnClose('hide');
        }
    }

    private _OnCancel(){
        // Cancelar desde el carrito: acción del camarero SELECCIONADO -> sellarlo para
        // la fila e0 del informe de actividad (si no, salía el creador de la comanda).
        this._ticket.StampWaiter();
        this._ticket.status = 'CC';
        this.place.DelCart(this._ticket);

        let _commit = !this._ticket.ToInsert;
        if (_commit){  // needs to be commited
            this._ticket.DoCommit();
        }

        this._OnClose('cancel');
    }

    async OnCancel(){
        if (!this._ticket.IsEmpty){
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@ticket_cancel_confirm_tittle'),
                message: this.lang.tr('@ticket_cancel_confirm_message'),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
    
                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);
                        return false;
                    }
                }]
            });
    
            alert.onDidDismiss().then((detail:any) => {
                if (detail.data == true){
                    this._OnCancel();
                }
            });
    
            await alert.present();            
        }
        else {
            this._OnCancel();
        }
    }

    async OnClear(){
        if (!this._ticket.IsEmpty){
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@ticket_remove_all_confirm_tittle'),
                message: this.lang.tr('@ticket_remove_all_confirm_message'),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
    
                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);
                        return false;
                    }
                }]
            });
    
            alert.onDidDismiss().then((detail:any) => {
                if (detail.data == true){
                    this._OnClose('empty');
                }
            });
    
            await alert.present();            
        }
        else {
            this._OnClose('empty');
        }
    }

    private _sending = false;
    get IsSending(){
        return this._sending;
    }

    set IsSending(value){
        this._sending = value;
        this.change.markForCheck();
    }

    async OnCommit(){
        if (this.view.Access == 'DEVRY'){
            this._OnClose('commit');
        }

        if (this.view.Access == 'GUEST'){
            this.IsSending = true;

            // Config del local: geolocalizacion QR desactivada → commit sin
            // pedir posicion (igual que OnSendTicket del footer).
            if (this.place.NoGeolocation || await this.geocode.NearBy(this.place.address)){
                this._ticket.comments = this._notes;
                if (this._ticket.IsEmpty){
                    this._ticket.status = 'DE';
                }
                else {
                    this._ticket.status = this.IsPrepay ? 'PR' : 'AC';
                }
        
                this.place.DelCart(this._ticket);
                this.toast.ShowWait('@sending_wait');
    
                let _commit = (this._ticket.status == 'CC') && (!this._ticket.ToUpdate);
                if (!_commit){  // needs to be commited
                    this._ticket.DoCommit().then(
                    data => {
                        this.IsSending = false;
    
                        if (this._ticket.status == 'AC'){
                            this.toast.ShowAlert('primary', this.lang.tr('@place_ticket_sent_toast'), 2000);
                        }        

                        // wait for ticket change printed
                        setTimeout(() => {  
                            this._OnClose('commit');
                            this.toast.HideWait();    
                        }, 100);
                    });
                }
            }
            else {
                this.IsSending = false;
            }

            this.geocode.NearBy(null);    
        }
    }
}
